import React, { useState } from "react";
import { usePastPerformancesForPreview } from "@/api/hooks/usePastPerformancesForPreview";
import { usePastPerformancesStore } from "@/stores/pastPerformancesStore";
import { X, Star } from "lucide-react";
import { Loader } from "@/components/ui/Loader";
import MaskExactProfessionalName from "../../utils/MaskExactProfessionalName";

interface ArtistPastPerformancesProps {
  hideTitle?: boolean;
  enableDelete?: boolean;
}

const ArtistPastPerformances: React.FC<ArtistPastPerformancesProps> = ({
  hideTitle,
  enableDelete = false,
}) => {
  const { performances } = usePastPerformancesForPreview();
  const deleteItem = usePastPerformancesStore((s) => s.deleteItem);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  return (
    <>
      <div className="px-4 md:mb-28 w-full lg:px-10">
        {!hideTitle && (
          <h3 className="text-[28px] text-center md:text-[42px] font-bold bg-gradient-to-r from-[#F16B2E] via-[#EC2179] to-[#EC2179] text-transparent bg-clip-text mt-16">
            PAST PERFORMANCES
          </h3>
        )}
        <div className="mt-10 w-full max-w-5xl mx-auto px-2 sm:px-4">
          <div className="space-y-4 sm:space-y-5">
            {performances.map((item, index) => {
              const isReal = item.id > 0;
              const canDelete = enableDelete && isReal;
              const isDeleting = deletingId === item.id;

              const handleDelete = async () => {
                if (!canDelete || deletingId !== null) return;
                try {
                  setDeletingId(item.id as number);
                  await deleteItem(item.id as number);
                } finally {
                  setDeletingId(null);
                }
              };

              const countLabel =
                item.event_count && item.event_count > 0
                  ? `${item.event_count}${item.event_count >= 10 ? "+" : ""} Events`
                  : "0 Events";

              return (
                <div
                  key={item.id ?? index}
                  className="group border bg-gray-900/80 shadow-[0_0_12px_rgba(255,255,255,0.2)] rounded-xl p-4 sm:p-6 backdrop-blur-sm hover:bg-gray-700/90 transition-all duration-300 hover:shadow-xl hover:scale-[1.01] relative"
                >
                  {canDelete && (
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={isDeleting}
                      className="absolute -top-2 -right-2 z-50 flex h-6 w-6 items-center justify-center rounded-full bg-black/80 text-xs text-white border border-white/40 disabled:opacity-60"
                    >
                      {isDeleting ? <Loader size="small" /> : <X className="h-3 w-3" />}
                    </button>
                  )}

                  {/* Mobile Layout - Stacked */}
                  <div className="flex flex-col space-y-3 sm:hidden">
                    <div className="flex items-center justify-center">
                      <div className="flex items-center font-semibold text-lg text-white">
                        <span className="text-xl text-pink-400 mr-2">★</span>
                        <span className="text-xl font-normal leading-none text-white">
                          <MaskExactProfessionalName value={item.event_name} />
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-center">
                      <div className="flex items-center font-medium text-gray-200 text-base">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-5 h-5 mr-2 text-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3" />
                          <circle cx="12" cy="12" r="10" />
                        </svg>
                        {countLabel}
                      </div>
                    </div>

                    <div className="flex items-center justify-center">
                      <div className="flex items-center text-gray-300 text-sm text-center">
                        <Star className="w-5 h-5 mr-2 text-indigo-500" />
                        <span className="break-words leading-relaxed">
                          <MaskExactProfessionalName value={item.desc || "—"} />
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Desktop / Tablet Layout - Horizontal */}
                  <div className="hidden sm:flex sm:items-center sm:justify-between sm:gap-6">
                    <div className="flex items-center font-semibold text-base lg:text-lg text-white min-w-0 flex-1">
                      <span className="text-xl lg:text-2xl mr-3 flex-shrink-0 text-pink-400">
                        ★
                      </span>
                      <span className="truncate">
                        <MaskExactProfessionalName value={item.event_name} />
                      </span>
                    </div>

                    <div className="flex items-center font-medium text-gray-200 text-sm lg:text-base flex-shrink-0">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-4 h-4 lg:w-5 lg:h-5 mr-2 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3" />
                        <circle cx="12" cy="12" r="10" />
                      </svg>
                      {countLabel}
                    </div>

                    <div className="flex items-center text-gray-300 text-sm lg:text-base min-w-0 flex-1 justify-end">
                      <Star className="w-5 h-5 mr-2 text-indigo-500" />
                      <span className="break-words mt-2 text-[14px] lg:text-[16px] font-[300] leading-[19.36px] text-white">
                        <MaskExactProfessionalName value={item.desc || "—"} />
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default ArtistPastPerformances;
